import { useEffect, useState, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import debounce from "lodash.debounce";
import { commonStrings } from "./StringBundle";
import Snackbar from "./Snackbar";

const AsyncAutocomplete = ({
  name,
  control,
  label,
  required = false,
  fetchOptions,
  disabled = false,
  labelKey = "label",
  valueKey = "id",
  dependentFieldName = "",
  hasAddNew = false,
}) => {
  const { control: contextControl } = useFormContext();
  const dependencyValue = useWatch({
    control: control || contextControl,
    name: dependentFieldName || "",
  });
  const labelWithAsterisk = required ? `${label} *` : label;

  // Base filter
  const getOptionLabel = (option) => {
    if (hasAddNew && option?.isAddNew && option?.typedValue)
      return `Add '${option?.typedValue}'`;
    return option?.[labelKey] || "";
  };
// isOptionEqualToValue function
  const isOptionEqualToValue = (option, value) =>
    option?.[valueKey] === value?.[valueKey];

  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showSnack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  // clear options if dependent field changes
  useEffect(() => {
    if (dependentFieldName) {
      setOptions([]);
    }
  }, [JSON.stringify(dependencyValue)]);

  // on poen dropdown call api
  useEffect(() => {
    if (open && options.length === 0) {
      loadOptions();
    }
  }, [open]);

  // Debounced search function
  const loadOptions = useMemo(
    () =>
      debounce(async (query) => {
        setLoading(true);
        setOptions([]);
        try {
          const results = await fetchOptions(query);
          if (query && query.trim().length > 0 && hasAddNew && !results?.length) {
            setOptions([{ isAddNew: true, typedValue: query.trim() }]);
          } else {
            setOptions(results || []);
          }
        } catch (error) {
          setSnack({
            open: true,
            message: commonStrings.serverError,
            severity: "error",
          });
        } finally {
          setLoading(false);
        }
      }, 500),
    [fetchOptions]
  );

  // on input change
  function onInputChange(value) {
    setInputValue(value);
    const trimmedValue = value.trim();
    const trimmedSearchedValue = inputValue?.trim();
    if (trimmedValue.length >= 3) {
      loadOptions(value);
    } else if (trimmedValue.length < 3 && trimmedSearchedValue.length >= 3) {
      loadOptions("");
    } else if (
      trimmedValue.length === 0 &&
      trimmedSearchedValue !== trimmedValue &&
      trimmedSearchedValue < 3
    ) {
      loadOptions("");
    }
  }

  // on change select
  function onChangeSelect(data, field) {
    if (hasAddNew && data?.isAddNew) {
      const newOption = {
        [labelKey]: data.typedValue,
        [valueKey]: data.typedValue,
        isCustom: true,
      };

      // Add to options list so it stays selectable
      setOptions((prev) => [...prev, newOption]);

      // Update form value
      field.onChange(newOption);
    } else {
      field.onChange(data);
    }
  }

    useEffect(() => {
    return () => {
      loadOptions.cancel();
    };
  }, [loadOptions]);

  return (
    <>
      {showSnack.open && (
        <Snackbar
          {...showSnack}
          handleClose={() =>
            setSnack({ open: false, message: "", severity: "" })
          }
        />
      )}
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => {
          return (
            <Autocomplete
              open={open}
              onOpen={() => {
                setOpen(true);
                setInputValue("");
                setOptions([]);
                loadOptions("");
              }}
              onClose={() => setOpen(false)}
              options={options}
              disabled={disabled}
              loading={loading}
              filterOptions={(options) => options}
              getOptionLabel={getOptionLabel}
              isOptionEqualToValue={isOptionEqualToValue}
              value={field.value || null}
              onChange={(_, data) => onChangeSelect(data, field)}
              onInputChange={(_, value, reason) => {
                if (reason === "input") {
                  onInputChange(value);
                } else if (reason === "clear") {
                  onInputChange("");
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={labelWithAsterisk}
                  error={!!error}
                  helperText={error?.message}
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: [
                      <>
                        {loading && <CircularProgress size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ],
                  }}
                />
              )}
            />
          );
        }}
      />
    </>
  );
};

export default AsyncAutocomplete;